/**
 * 创建广告页（/platform/campaign）session 草稿持久化
 *
 * createInstance：createScript.js 组件实例（this）
 * session key：`${email}_${CAMPAIGN_CREATE_SESSION_KEY_SUFFIX}`
 *
 * 行为约定：
 * - 进入页：有 session 则自动恢复（createScript created 调用 restoreCampaignCreateFromSession）
 * - 离开页：有草稿内容则弹确认框，用户确认后才写入 session
 * - 刷新（F5）/ 关闭标签：清除 session，不保存未确认的编辑
 * - 提交成功：clearCache 清除 session
 *
 * session 结构（collectCampaignCreateSessionState 产出）：
 * {
 *   isCopy, copySourceId,           // 复制来源标记
 *   active, activeName, createType, // 步骤位置
 *   currencyListFlag,               // 货币标记
 *   form: { accountId, campaign, adSet, ad, ... },
 *   cpc: { chargeType, targetCpa, ... },
 *   cpcInfo: { chargeType, targetBid, ... },
 *   ads: [],                        // 广告列表
 *   domainWhitelist: { correctList, ... }  // 站点白名单 UI 状态
 * }
 */
import { CAMPAIGN_CREATE_SESSION_KEY_SUFFIX } from './constants';
import { getDashboardSessionEmail } from './dashboardSession';

/** 默认 UTM 追踪模板 */
const DEFAULT_AD_UTM = 'utm_source=popin&utm_medium=referral&utm_campaign=${CAMPAIGN_NAME}'
    + '&utm_content=${AD_TITLE}&utm_term=${CONTENT_NAME}&tracking_id=${TRACKING_ID}';

// ─────────────────────────────────────────────────────
// 字段白名单定义
// ─────────────────────────────────────────────────────

/**
 * campaign 表单按层级分组的白名单
 * - true：直接取根级字段值
 * - string[]：从对应子对象中按 key 提取
 */
const CACHE_FIELD_MAP = {
    accountId: true,
    productName: true,
    location: true,
    country: true,
    ad: ['brandName', 'landingPage', 'utmTracking', 'impURL', 'clickTracking'],
    campaign: [
        'campaignObjective', 'monthlyCapStatus', 'campaignName', 'campaignStatus',
        'startType', 'startTime', 'endType', 'endTime',
        'dayParting', 'dayPartingStatus',
        'dailyCap', 'lifetimeCap', 'dailyCapJpy', 'lifetimeCapJpy',
        'uniformlyFlag', 'impURL', 'clickTracking', 'productId', 'pb',
        'dpTimezone', 'imageFit', 'tracking_urls', 'config_status'
    ],
    adSet: [
        'industry', 'audience', 'targetBid', 'targetBidJpy',
        'platform', 'os', 'browser', 'osv', 'gender', 'carrier',
        'userPackage', 'location', 'adsetName', 'fileId'
    ]
};

/** 根级出价字段（与 chargeType 联动） */
const CPC_ROOT_FIELDS = [
    'chargeType', 'targetCpa', 'mcpa', 'optimizationType',
    'targetMin', 'targetMax', 'targetBido', 'targetCpao', 'targetMaxo', 'targetBide'
];

/** bidTarget 组件 cpcInfo 中需要快照的字段 */
const CPC_INFO_FIELDS = ['chargeType', 'targetCpa', 'targetBido', 'targetCpao', 'targetBid', 'mcpa'];

// ─────────────────────────────────────────────────────
// 通用检测工具
// ─────────────────────────────────────────────────────

/** 字符串非空（排除 null / undefined / 纯空白） */
function hasText(value) {
    return value !== null && value !== undefined && String(value).trim() !== '';
}

/** 多选数组是否与默认值不同（默认 ['All'] 或 [] 视为未填写） */
function isNonDefaultArray(value, defaultValue = ['All']) {
    return Array.isArray(value) && value.length > 0 && JSON.stringify(value) !== JSON.stringify(defaultValue);
}

/** 地域定向是否包含有效选择（递归检查 location 树） */
function hasLocation(location = {}) {
    if (!location || typeof location !== 'object') return false;
    return Object.values(location).some(fieldValue => {
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') return false;
        if (Array.isArray(fieldValue)) return fieldValue.length > 0 && !(fieldValue.length === 1 && (fieldValue[0] === 'All' || fieldValue[0] === ''));
        if (typeof fieldValue === 'object') return hasLocation(fieldValue);
        return true;
    });
}

/** 默认分时段矩阵：7 天 × 24 小时全 1（全天投放） */
function defaultDayParting() {
    return Array.from({ length: 7 }, () => Array(24).fill(1));
}

// ─────────────────────────────────────────────────────
// 草稿检测
// ─────────────────────────────────────────────────────

/** 单个广告组是否含有可持久化的草稿内容 */
function hasAdGroupDraft(adGroup = {}) {
    return !!(
        adGroup.fileArr?.length
        || hasText(adGroup.brandName)
        || hasText(adGroup.landingPage)
        || hasText(adGroup.creativeInfo)
        || hasText(adGroup.impURL)
        || hasText(adGroup.clickTracking)
        || isNonDefaultArray(adGroup.industry, [])
        || (adGroup.utmTracking && adGroup.utmTracking !== DEFAULT_AD_UTM)
        || hasText(adGroup.headline?.title || adGroup.headlines?.[0]?.title || '')
    );
}

/**
 * 当前场景是否启用创建广告 session
 * 编辑已有 campaign、追加素材时不启用；复制 Campaign 启用
 */
export function shouldUseCampaignCreateSession(inst) {
    if (!inst || inst.isAddNewAssets) return false;
    if (inst.isCopy) return true;
    return !inst.campaignId;
}

/** 复制页 session 是否与当前来源 campaign 匹配 */
export function hasCopyCampaignCreateSession(inst) {
    if (!inst?.isCopy || !inst.campaignId) return false;
    const session = readCampaignCreateSession(inst);
    return session?.isCopy && String(session.copySourceId) === String(inst.campaignId);
}

/**
 * 表单是否含有值得保存的草稿内容
 *
 * 检查顺序（短路评估，有任一内容即返回 true）：
 * 1. 步骤位置 > 0（已进入第二步）
 * 2. 创建类型已选
 * 3. 根级字段（accountId / productName / country / location）
 * 4. campaign 子表单字段
 * 5. adSet 子表单字段
 * 6. 出价相关字段（CPC_ROOT_FIELDS + cpcInfo）
 * 7. 广告组内容（ad / ads）
 * 8. 站点白名单（correctList）
 */
export function hasCampaignCreateDraftContent(inst) {
    if (!shouldUseCampaignCreateSession(inst)) return false;
    if (inst.active > 0 || inst.createType) return true;

    const form = inst.campaign || {};

    // 根级字段
    if (hasText(form.accountId) || hasText(form.productName) || form.country?.length) return true;
    if (hasLocation(form.location)) return true;

    // campaign 子表单
    const campaignForm = form.campaign || {};
    const campaignTextFields = [
        'campaignObjective', 'campaignName', 'dailyCap', 'lifetimeCap',
        'dailyCapJpy', 'lifetimeCapJpy', 'impURL', 'clickTracking', 'productId', 'pb'
    ];
    if (campaignTextFields.some(key => hasText(campaignForm[key]))) return true;
    if (campaignForm.monthlyCapStatus) return true;
    if (campaignForm.startType && campaignForm.startType !== 'immediately') return true;
    if (campaignForm.endType && campaignForm.endType !== 'indefinitely') return true;
    if (campaignForm.dayPartingStatus === 'on') return true;
    if (Array.isArray(campaignForm.tracking_urls) && campaignForm.tracking_urls.length) return true;

    // adSet 子表单
    const adSetForm = form.adSet || {};
    if (isNonDefaultArray(adSetForm.industry, []) || isNonDefaultArray(adSetForm.audience)) return true;
    if (['platform', 'os', 'browser', 'gender', 'carrier'].some(key => isNonDefaultArray(adSetForm[key]))) return true;
    if (hasText(adSetForm.targetBid) || hasText(adSetForm.targetBidJpy)) return true;
    if (hasText(adSetForm.osv) || hasText(adSetForm.userPackage)) return true;
    if (hasLocation(adSetForm.location)) return true;

    // 出价字段
    if (CPC_ROOT_FIELDS.some(key => hasText(form[key]))) return true;
    const cpcInfo = inst.cpcInfo || {};
    if (CPC_INFO_FIELDS.some(key => hasText(cpcInfo[key]))) return true;

    // 广告组
    if (hasAdGroupDraft(form.ad || {})) return true;
    if ((form.ads || []).some(hasAdGroupDraft)) return true;

    // 站点白名单
    if (hasText(inst.correctList)) return true;

    return false;
}

/** 离开创编页时是否弹保存确认框 */
export function shouldConfirmLeaveCampaignCreate(inst) {
    return hasCampaignCreateDraftContent(inst);
}

// ─────────────────────────────────────────────────────
// Session 读写
// ─────────────────────────────────────────────────────

/** 生成 sessionStorage key */
export function getCampaignCreateSessionKey(email) {
    return email ? `${email}_${CAMPAIGN_CREATE_SESSION_KEY_SUFFIX}` : '';
}

/** 读取创建广告 session */
export function readCampaignCreateSession(instOrEmail) {
    const email = typeof instOrEmail === 'object' ? getDashboardSessionEmail(instOrEmail) : instOrEmail;
    const key = getCampaignCreateSessionKey(email);
    if (!key) return null;
    try { return JSON.parse(sessionStorage.getItem(key)); }
    catch { return null; }
}

/** 写入创建广告 session */
export function writeCampaignCreateSession(instOrEmail, payload) {
    const email = typeof instOrEmail === 'object' ? getDashboardSessionEmail(instOrEmail) : instOrEmail;
    const key = getCampaignCreateSessionKey(email);
    if (!key) return;
    try { sessionStorage.setItem(key, JSON.stringify(payload)); }
    catch { /* ignore */ }
}

/** 清除当前用户的创建广告 session */
export function clearCampaignCreateSession(instOrEmail) {
    const email = typeof instOrEmail === 'object' ? getDashboardSessionEmail(instOrEmail) : instOrEmail;
    const key = getCampaignCreateSessionKey(email);
    if (key) sessionStorage.removeItem(key);
}

/** 登出时清除创建广告 session */
export function clearCampaignCreateSessionOnLogout(email) {
    if (email) { clearCampaignCreateSession(email); return; }
    const suffix = `_${CAMPAIGN_CREATE_SESSION_KEY_SUFFIX}`;
    Object.keys(sessionStorage).forEach(key => { if (key.endsWith(suffix)) sessionStorage.removeItem(key); });
}

/** 是否存在可恢复的创建广告 session */
export function hasCampaignCreateSession(inst) {
    return !!readCampaignCreateSession(inst);
}

// ─────────────────────────────────────────────────────
// Session 采集（写入前的数据收集）
// ─────────────────────────────────────────────────────

/** 从 campaign 表单按白名单提取可序列化片段 */
function pickFormFields(form = {}) {
    const picked = Object.create(null);
    Object.entries(CACHE_FIELD_MAP).forEach(([section, config]) => {
        if (config === true) {
            picked[section] = form[section];
        } else if (Array.isArray(config)) {
            picked[section] = {};
            config.forEach(key => {
                if (form[section]?.[key] !== undefined) picked[section][key] = form[section][key];
            });
        }
    });

    // 规范化日期和分时段默认值
    const campaignSection = picked.campaign;
    if (campaignSection) {
        if (campaignSection.startType === 'immediately') campaignSection.startTime = null;
        if (campaignSection.endType === 'indefinitely') campaignSection.endTime = null;
        if (campaignSection.dayPartingStatus === 'off') campaignSection.dayParting = defaultDayParting();
        if (campaignSection.lifetimeCap === '0') campaignSection.lifetimeCap = '';
    }
    return picked;
}

/**
 * 将 bidTarget 的 cpcInfo 同步到 campaign 表单
 * 持久化前调用，确保表单中的出价字段与 bidTarget 组件状态一致
 */
export function syncCpcInfoToCampaignForm(inst) {
    const { cpcInfo, campaign: form } = inst;
    if (!cpcInfo || !form) return;

    if (cpcInfo.chargeType !== undefined && cpcInfo.chargeType !== '') {
        form.chargeType = cpcInfo.chargeType;
    }
    if (cpcInfo.targetCpa !== undefined) form.targetCpa = cpcInfo.targetCpa;
    if (cpcInfo.targetBido !== undefined) form.targetBido = cpcInfo.targetBido;
    if (cpcInfo.targetCpao !== undefined) form.targetCpao = cpcInfo.targetCpao;
    if (cpcInfo.mcpa !== undefined) form.mcpa = cpcInfo.mcpa;
    if (cpcInfo.targetBid !== undefined && form.adSet) form.adSet.targetBid = cpcInfo.targetBid;

    // 用户包 UI 绑定数组，提交/session 用分号串
    if (Array.isArray(inst.userPackage) && inst.userPackage.length) {
        form.adSet.userPackage = inst.userPackage.join(';');
    }
}

/** 从 cpcInfo 提取出价字段快照 */
function pickCpcInfoSnapshot(cpcInfo = {}) {
    return CPC_INFO_FIELDS.reduce((result, key) => {
        if (cpcInfo[key] != null) result[key] = cpcInfo[key];
        return result;
    }, {});
}

/** 从 campaign 根表单反推 cpcInfo 结构（恢复时兜底） */
function buildCpcInfoFromForm(form = {}) {
    return {
        chargeType: form.chargeType || '',
        targetCpa: form.targetCpa || '',
        targetBido: form.targetBido || '',
        targetCpao: form.targetCpao || '',
        targetBid: form.adSet?.targetBid || '',
        mcpa: form.mcpa || ''
    };
}

/** 收集 createScript 当前状态为 session 快照 */
export function collectCampaignCreateSessionState(inst) {
    syncCpcInfoToCampaignForm(inst);
    const form = inst.campaign || {};

    const cpc = CPC_ROOT_FIELDS.reduce((result, key) => { result[key] = form[key]; return result; }, {});

    // 站点白名单 UI 状态（不在 campaign 表单内）
    const domainWhitelist = {};
    if (inst.correctList) {
        domainWhitelist.correctList = inst.correctList;
        domainWhitelist.correctLenght = inst.correctLenght || '';
        domainWhitelist.successDomain = !!inst.successDomain;
        domainWhitelist.downFileId = inst.downFileId || '';
    }

    return {
        isCopy: !!inst.isCopy,
        copySourceId: inst.isCopy && inst.campaignId ? String(inst.campaignId) : '',
        active: inst.active || 0,
        activeName: inst.activeName || 'singleUpload',
        createType: inst.createType || '',
        currencyListFlag: inst.currencyListFlag || '',
        form: pickFormFields(form),
        cpc,
        cpcInfo: pickCpcInfoSnapshot(inst.cpcInfo),
        ads: JSON.parse(JSON.stringify(form.ads || [])),
        domainWhitelist
    };
}

// ─────────────────────────────────────────────────────
// Session 持久化 & 确认框
// ─────────────────────────────────────────────────────

/**
 * 将当前表单写入 sessionStorage
 * @param {Object} options.force  为 true 时跳过草稿内容检测（离开确认后写入）
 */
export function persistCampaignCreateSession(inst, options = {}) {
    if (inst._isRestoringCampaignSession) return;
    if (!shouldUseCampaignCreateSession(inst)) return;
    if (!options.force && !hasCampaignCreateDraftContent(inst)) {
        clearCampaignCreateSession(inst);
        return;
    }
    const email = getDashboardSessionEmail(inst);
    if (email) writeCampaignCreateSession(email, collectCampaignCreateSessionState(inst));
}

/** 弹出「是否保存新建广告草稿」确认框，返回 Promise<boolean> */
export function confirmLeaveCampaignCreate(inst) {
    return inst.$confirm(
        inst.$t('msn.validations.cacheSaveNewCampaign'),
        inst.$t('msn.infos.info'),
        {
            type: 'warning',
            customClass: 'msn-message-box',
            showClose: false,
            cancelButtonText: inst.$t('msn.button.cancel'),
            confirmButtonText: inst.$t('msn.button.confirm'),
            cancelButtonClass: 'msn-cancel-button',
            confirmButtonClass: 'msn-confirm-button'
        }
    ).then(() => true).catch(() => false);
}

// ─────────────────────────────────────────────────────
// Session 恢复（还原到 createScript 实例）
// ─────────────────────────────────────────────────────

/**
 * 将 session 快照还原到 createScript 实例
 *
 * 恢复步骤：
 * 1. 反序列化日期字段（startTime / endTime）为 Date 对象
 * 2. 深度合并 session 表单与当前默认表单（session 字段覆盖默认值）
 * 3. 按 chargeType 选择性恢复出价字段（cpc / ocpc / ecpc / max_cv 各有不同字段集）
 * 4. 恢复步骤位置、创建类型、货币标记
 * 5. 恢复 bidTarget 组件内部出价状态（cpcInfo）
 * 6. nextTick 中恢复地域定向（需 locationCpn $refs 就绪）和广告列表
 *
 * @returns {boolean} 是否成功应用
 */
export function applyCampaignCreateSessionState(inst, session) {
    if (!session?.form) return false;

    const cached = session.form;
    const cachedCpc = session.cpc || {};
    const cachedAds = session.ads || [];

    // 反序列化日期字段
    if (cached.campaign) {
        cached.campaign.startTime = cached.campaign.startTime ? new Date(cached.campaign.startTime) : new Date();
        cached.campaign.endTime = cached.campaign.endTime ? new Date(cached.campaign.endTime) : '';
        cached.campaign.uniformlyFlag = String(cached.campaign.uniformlyFlag ?? '0');
    }

    // 深度合并表单（session 覆盖默认值，三个子对象分别合并避免丢失未缓存字段）
    const merged = Object.assign({}, inst.campaign, cached);
    merged.campaign = Object.assign({}, inst.campaign.campaign, cached.campaign || {});
    merged.adSet = Object.assign({}, inst.campaign.adSet, cached.adSet || {});
    merged.ad = Object.assign({}, inst.campaign.ad, cached.ad || {});
    if (cachedAds.length) merged.ads = cachedAds;

    // 按 chargeType 恢复出价字段
    const chargeType = cachedCpc.chargeType || merged.chargeType;
    const bid = merged.adSet?.targetBid;

    inst.campaign = {
        ...merged,
        ...cachedCpc,
        adSet: { ...merged.adSet, targetBid: chargeType === 'cpc' ? bid : '' },
        targetBido: chargeType === 'ocpc' ? (cachedCpc.targetBido || bid || '') : '',
        targetCpao: ['ocpc', 'max_cv'].includes(chargeType)
            ? (cachedCpc.targetCpao || merged.targetCpao || merged.cpa || '') : '',
        mcpa: chargeType === 'ocpc' ? (cachedCpc.mcpa || merged.mcpa || '') : '',
        targetMaxo: chargeType === 'ocpc' ? (cachedCpc.targetMaxo || merged.targetMax || '101') : '101',
        targetBide: chargeType === 'ecpc' ? (cachedCpc.targetBide || bid || '') : '',
        targetMin: chargeType === 'ecpc' ? (cachedCpc.targetMin || '60') : '60',
        targetMax: chargeType === 'ecpc' ? (cachedCpc.targetMax || '200') : '200'
    };

    // 恢复步骤和创建类型
    inst.active = session.active > 0 ? 1 : 0;
    inst.activeName = session.activeName || 'singleUpload';
    inst.createType = session.createType || '';
    inst.currencyListFlag = session.currencyListFlag || inst.currencyListFlag;

    // 恢复 cpcInfo
    const restoredCpcInfo = session.cpcInfo
        ? { ...buildCpcInfoFromForm(inst.campaign), ...session.cpcInfo }
        : buildCpcInfoFromForm(inst.campaign);
    inst.cpcInfo = { ...inst.cpcInfo, ...restoredCpcInfo };
    syncCpcInfoToCampaignForm(inst);

    // 恢复用户包（UI 绑定数组，提交时用分号串）
    const userPackageStr = merged.adSet?.userPackage;
    inst.userPackage = hasText(userPackageStr) ? String(userPackageStr).split(';').filter(Boolean) : [];

    // 恢复第三方追踪（显式赋值确保 Vue 2 响应式追踪）
    if (cached.campaign) {
        if (Array.isArray(cached.campaign.tracking_urls) && cached.campaign.tracking_urls.length) {
            inst.campaign.campaign.tracking_urls = cached.campaign.tracking_urls;
        }
        if (cached.campaign.config_status !== undefined) {
            inst.campaign.campaign.config_status = cached.campaign.config_status;
        }
    }

    // 恢复站点白名单 UI 状态
    const domainWhitelist = session.domainWhitelist;
    if (domainWhitelist?.correctList) {
        inst.correctList = domainWhitelist.correctList;
        inst.correctLenght = domainWhitelist.correctLenght || '';
        inst.successDomain = !!domainWhitelist.successDomain;
        inst.downFileId = domainWhitelist.downFileId || '';
    }

    // nextTick 中恢复依赖 $refs 的状态（地域定向、出价组件、广告列表）
    inst.$nextTick(() => {
        if (inst.$refs.locationCpn && cached.location) {
            inst.$refs.locationCpn.setParams(cached.location);
            inst.newLocation = inst.$refs.locationCpn.getParams();
        }
        if (typeof inst.handleCpcInfo === 'function') inst.handleCpcInfo();
        if (typeof inst.adListChange === 'function') inst.adListChange();
    });

    return true;
}
